/** The editor's unsaved state (panels, obstacles, roof edits) with undo and redo, and what a save sends.
 *
 * Dirty means "differs from the saved version": every change recomputes `dirty` by comparing the normalised
 * document (what a save would send: editorPanels / editorObstacles, rounded) with the one loaded (`base`). Loading,
 * viewing, a click that snaps a panel back to its own slot, a face toggled off and on, or an undo back to the start
 * are therefore never unsaved changes. A saved panel moved by less than a millimetre (a drag away and back, which the
 * grid snap can land a fraction off) counts as where it was: the comparison and the save both use its saved values
 * (`settledPanels`), so the editor never asks to save, nor saves, a change nobody can see. */
import type { S } from "../api/client";
import type { Vec2 } from "./geo.ts";
import { NO_ROOF_EDITS, type RoofEdits } from "./roof.ts";
import type { Orientation } from "./snap.ts";

export interface PanelItem {
  /** Local identity (stable while editing). */
  key: string;
  /** Server id; null for a panel added in this session. */
  id: string | null;
  face_id: string;
  orientation: Orientation;
  center: Vec2;
  rotation: number;
}

export interface ObstacleItem {
  key: string;
  id: string | null;
  kind: S["ObstructionKind"];
  polygon: Vec2[];
  height_m: number | null;
  source: string;
  confidence: number | null;
  /** How a drawn obstacle stands (Sketch: a box or a cylinder); detected ones have none. */
  shape?: S["ObstructionShape"] | null;
}

export interface Draft {
  panels: PanelItem[];
  obstacles: ObstacleItem[];
  roof: RoofEdits;
  /** Whether each part differs from the saved version (derived: `withDirty`). */
  dirty: { panels: boolean; obstacles: boolean; roof: boolean };
  /** The saved version, normalised (absent: an empty design). */
  base?: DraftBase;
}

/** What the loaded version holds, in the form a save sends it. */
export interface DraftBase {
  panels: string;
  /** The saved panels by id, as a save sends them (`settledPanels` compares against them). */
  saved?: SavedPanels;
  obstacles: string;
  /** Roof vertices' plan positions and faces' planes, rounded as a save rounds them. */
  vertices: Record<string, Vec2>;
  faces: Record<string, { pitch_deg: number; azimuth_deg: number }>;
}

export interface History {
  past: Draft[];
  present: Draft;
  /** Undone states, most recent last (cleared by any new change). */
  future?: Draft[];
}

let counter = 0;
export const newKey = (prefix: string) => `${prefix}-${++counter}`;

export function draftFrom(doc: S["EditorDocument"]): Draft {
  const draft: Draft = {
    panels: doc.panels.map((p) => ({
      key: newKey("p"),
      id: p.id,
      face_id: p.face_id,
      orientation: p.orientation as Orientation,
      center: p.center_uv as Vec2,
      rotation: p.rotation_deg ?? 0,
    })),
    obstacles: doc.obstructions.map((o) => ({
      key: newKey("o"),
      id: o.id,
      kind: o.kind,
      polygon: o.polygon as Vec2[],
      height_m: o.height_m,
      source: o.source,
      confidence: o.confidence,
      shape: o.shape ?? null,
    })),
    roof: NO_ROOF_EDITS,
    dirty: { panels: false, obstacles: false, roof: false },
  };
  const roof = doc.roof_model;
  const saved = savedPanelsOf(draft.panels);
  draft.base = {
    panels: panelsSignature(draft.panels, saved),
    saved,
    obstacles: obstaclesSignature(draft.obstacles),
    vertices: Object.fromEntries((roof?.vertices ?? []).map((v) => [v.id, [round(v.xyz[0]!), round(v.xyz[1]!)] as Vec2])),
    faces: Object.fromEntries((roof?.faces ?? []).map((f) => [f.id, { pitch_deg: f.plane.pitch_deg, azimuth_deg: f.plane.azimuth_deg }])),
  };
  return draft;
}

export type SavedPanels = Record<string, S["EditorPanel"]>;

/** How far a saved panel may be from its saved place and still be "where it was": under a millimetre, a hundredth of
 * a degree (a save sends 0.1 mm and 0.001°; nobody places a panel finer than a millimetre). */
export const PANEL_TOLERANCE_M = 0.001;
export const PANEL_TOLERANCE_DEG = 0.01;

/** The saved panels by id, as a save sends them. */
export function savedPanelsOf(panels: PanelItem[]): SavedPanels {
  const out: SavedPanels = {};
  for (const p of editorPanels(panels)) if (p.id) out[p.id] = p;
  return out;
}

/** The panels as a save sends them, a saved panel within the tolerance of its saved place sent as saved. */
export function settledPanels(panels: PanelItem[], saved?: SavedPanels): S["EditorPanel"][] {
  return editorPanels(panels).map((p) => {
    const was = p.id ? saved?.[p.id] : undefined;
    if (!was || was.face_id !== p.face_id || was.orientation !== p.orientation) return p;
    const [u, v] = p.center_uv as Vec2;
    const [u0, v0] = was.center_uv as Vec2;
    const near = Math.hypot(u - u0, v - v0) < PANEL_TOLERANCE_M && Math.abs((p.rotation_deg ?? 0) - (was.rotation_deg ?? 0)) < PANEL_TOLERANCE_DEG;
    return near ? was : p;
  });
}

/** The panels as a save sends them, order-free: equal signatures mean nothing to save. */
export function panelsSignature(panels: PanelItem[], saved?: SavedPanels): string {
  return JSON.stringify(settledPanels(panels, saved).map((p) => JSON.stringify(p)).sort());
}

/** The obstacles as a save sends them, order-free. */
export function obstaclesSignature(obstacles: ObstacleItem[]): string {
  return JSON.stringify(editorObstacles(obstacles).map((o) => JSON.stringify(o)).sort());
}

const EMPTY_BASE: DraftBase = { panels: "[]", obstacles: "[]", vertices: {}, faces: {} };
const sameAngle = (a: number | undefined, b: number) => a == null || Math.abs(a - b) < 1e-3;

/** Whether roof edits move a vertex or change a face's plane (as a save would send them). */
export function roofEdited(roof: RoofEdits, base: DraftBase = EMPTY_BASE): boolean {
  for (const [id, xy] of Object.entries(roof.vertices)) {
    const b = base.vertices[id];
    if (!b || round(xy[0]) !== b[0] || round(xy[1]) !== b[1]) return true;
  }
  for (const [id, e] of Object.entries(roof.faces)) {
    const b = base.faces[id];
    if (!b) return e.pitch_deg != null || e.azimuth_deg != null;
    if (!sameAngle(e.pitch_deg, b.pitch_deg) || !sameAngle(e.azimuth_deg, b.azimuth_deg)) return true;
  }
  return false;
}

/** The draft with `dirty` recomputed against the saved version. Unchanged parts keep their flag cheaply. */
export function withDirty(next: Draft, prev?: Draft): Draft {
  const base = next.base ?? EMPTY_BASE;
  const dirty = {
    panels: prev && next.panels === prev.panels ? prev.dirty.panels : panelsSignature(next.panels, base.saved) !== base.panels,
    obstacles: prev && next.obstacles === prev.obstacles ? prev.dirty.obstacles : obstaclesSignature(next.obstacles) !== base.obstacles,
    roof: prev && next.roof === prev.roof ? prev.dirty.roof : roofEdited(next.roof, base),
  };
  const same = dirty.panels === next.dirty.panels && dirty.obstacles === next.dirty.obstacles && dirty.roof === next.dirty.roof;
  return same ? next : { ...next, dirty };
}

export type Action =
  | { type: "reset"; draft: Draft }
  | { type: "panels"; panels: PanelItem[]; transient?: boolean }
  | { type: "obstacles"; obstacles: ObstacleItem[] }
  | { type: "roof"; roof: RoofEdits; transient?: boolean }
  | { type: "checkpoint" }
  | { type: "undo" }
  | { type: "redo" };

const MAX_UNDO = 60;

/** Undoable reducer; `transient` updates (a drag in progress) do not add undo steps until a `checkpoint`. */
export function reduce(h: History & { pending?: Draft | null }, a: Action): History & { pending?: Draft | null } {
  const push = (next: Draft, transient?: boolean) => {
    if (transient) return { past: h.past, present: next, pending: h.pending ?? h.present, future: [] };
    return { past: [...h.past, h.pending ?? h.present].slice(-MAX_UNDO), present: next, pending: null, future: [] };
  };
  switch (a.type) {
    case "reset":
      return { past: [], present: a.draft, pending: null, future: [] };
    case "panels":
      return push(withDirty({ ...h.present, panels: a.panels }, h.present), a.transient);
    case "obstacles":
      return push(withDirty({ ...h.present, obstacles: a.obstacles }, h.present));
    case "roof":
      return push(withDirty({ ...h.present, roof: a.roof }, h.present), a.transient);
    case "checkpoint":
      return h.pending ? { ...h, past: [...h.past, h.pending].slice(-MAX_UNDO), pending: null } : h;
    case "undo": {
      const previous = h.past[h.past.length - 1];
      return previous
        ? { past: h.past.slice(0, -1), present: previous, pending: null, future: [...(h.future ?? []), h.present].slice(-MAX_UNDO) }
        : h;
    }
    case "redo": {
      const next = h.future?.[h.future.length - 1];
      return next ? { past: [...h.past, h.present].slice(-MAX_UNDO), present: next, pending: null, future: h.future!.slice(0, -1) } : h;
    }
  }
}

export function isDirty(d: Draft): boolean {
  return d.dirty.panels || d.dirty.obstacles || d.dirty.roof;
}

export function editorPanels(panels: PanelItem[]): S["EditorPanel"][] {
  return panels.map((p) => ({
    id: p.id,
    face_id: p.face_id,
    orientation: p.orientation,
    center_uv: [round(p.center[0]), round(p.center[1])],
    rotation_deg: Math.round(p.rotation * 1000) / 1000,
  }));
}

export function editorObstacles(obstacles: ObstacleItem[]): S["EditorObstruction"][] {
  return obstacles.map((o) => ({
    id: o.id,
    kind: o.kind,
    polygon: o.polygon.map(([x, y]) => [round(x), round(y)]),
    height_m: o.height_m,
    ...(o.shape ? { shape: o.shape } : {}),
  }));
}

/** The body of `POST /designs/{id}/edits`: only what changed. */
export function editBody(base: number, d: Draft, note: string): S["DesignEdit"] {
  const body: S["DesignEdit"] = { base_version: base, note: note.trim() || null };
  if (d.dirty.roof)
    body.roof = {
      vertices: Object.entries(d.roof.vertices).map(([id, xy]) => ({ id, xy: [round(xy[0]), round(xy[1])] })),
      faces: Object.entries(d.roof.faces).map(([id, e]) => ({ id, ...e })),
    };
  if (d.dirty.obstacles) body.obstructions = editorObstacles(d.obstacles);
  if (d.dirty.panels) body.panels = settledPanels(d.panels, d.base?.saved);
  return body;
}

function round(v: number): number {
  return Math.round(v * 10000) / 10000;
}

/** A circle drawn as an obstacle: a 16-gon. */
export function circlePolygon(center: Vec2, radius: number, sides = 16): Vec2[] {
  return Array.from({ length: sides }, (_, i) => {
    const a = (2 * Math.PI * i) / sides;
    return [center[0] + radius * Math.cos(a), center[1] + radius * Math.sin(a)] as Vec2;
  });
}
